import React from 'react';
import { Await, useLoaderData, useSubmit } from 'react-router-dom';

import { DataTable } from '@oysterjs/ui/data-table';
import { SearchFilterInput, useFiltersFromLocalStorage } from '@oysterjs/ui/search-filter';

import {
  Action,
  ExcelSpreadsheetTemplateFieldsFragment,
  Resource,
  UpdateExcelSpreadsheetTemplateMutation,
  VisibilityConfigInput
} from '~/api/graphql';
import { PermissionButton, usePermissions } from '~/components/permissions';
import { BulkVisibilityDialog } from '~/components/visibility/bulk-visibility-dialog';
import { useVisibilityEntitlement } from '~/components/visibility/use-visibility-entitlement';

import { ArchiveConfirmDialog } from '~/components/list/archive-confirm-dialog';
import { ExcelTemplateCreateDialog } from '~/components/library/excel-template-create-dialog';
import { ExcelTemplateEditor } from '~/components/library/excel-template-editor';
import {
  excelTemplateRowHref,
  excelTemplateSortFn,
  getExcelTemplateColumns,
  type ExcelTemplateSortKey
} from '~/components/library/excel-template-columns';
import { FeatureDocCallout } from '~/components/library/feature-doc-callout';
import { FloatingSelectionBar } from '~/components/list/floating-selection-bar';
import { LibraryEditOverlayLoader } from '~/components/library/library-edit-overlay-loader';
import { LibraryListEmptyState } from '~/components/list/library-list-empty-state';
import { LibraryListLoading } from '~/components/list/library-list-loading';
import { LibraryListOutlet } from '~/components/library/library-list-outlet';
import {
  createStyleFilterDefinitions,
  filterStyleTemplates,
  type StyleFilter
} from '~/api/data/html-templates/styles-filters';
import { useActionValidationErrors } from '~/components/library/hooks/use-action-validation-errors';
import { useCurrentViewer } from '~/components/library/hooks/use-current-viewer';
import { useFloatingSelection } from '~/components/list/use-floating-selection-bar';
import { useLibraryBulkArchive } from '~/components/list/use-library-bulk-archive';
import { useLibraryBulkVisibility } from '~/components/library/hooks/use-library-bulk-visibility';

const ARCHIVE_PATH = '/app/library/excel-templates/archive';
const VISIBILITY_PATH = '/app/library/excel-templates/visibility';

const CREATE_EXCEL_TEMPLATE = [
  { resource: Resource.ExcelSpreadsheetTemplate, action: Action.Create }
];
const UPDATE_EXCEL_TEMPLATE = [
  { resource: Resource.ExcelSpreadsheetTemplate, action: Action.Update }
];

/**
 * Excel templates tab of the library. Lists uploaded .xlsx workbooks with an
 * Office Online preview on edit. Bulk actions go through router actions so the
 * parent loader revalidates automatically.
 */
export const LibraryExcelTemplatesPage = () => {
  const loader = useLoaderData() as {
    data?: Promise<{ excelSpreadsheetTemplates?: ExcelSpreadsheetTemplateFieldsFragment[] }>;
  };

  const viewer = useCurrentViewer();
  const visibilityEntitled = useVisibilityEntitlement();
  const canUpdate = usePermissions().hasAllPermissions(UPDATE_EXCEL_TEMPLATE);

  const [uploadDialogOpen, setUploadDialogOpen] = React.useState(false);

  const filterDefinitions = React.useMemo(() => createStyleFilterDefinitions(), []);
  const { activeFilters, setActiveFilters, initialFilters, sortBy, sortDirection, setSort } =
    useFiltersFromLocalStorage<StyleFilter, ExcelTemplateSortKey>(
      'cara_excel_templates_default_view',
      filterDefinitions,
      { defaultSortDirection: 'desc' }
    );

  const { selected, setSelected, selectedSet, clear } = useFloatingSelection();

  const archive = useLibraryBulkArchive({
    actionPath: ARCHIVE_PATH,
    onArchived: clear
  });

  const visibility = useLibraryBulkVisibility({
    actionPath: VISIBILITY_PATH,
    itemPlural: 'Excel templates',
    onUpdated: clear
  });

  return (
    <div className="space-y-4">
      <FeatureDocCallout
        variant="sky"
        title="Excel templates"
        description="Upload .xlsx workbooks. Cara fills them tab by tab to generate spreadsheets directly from a workspace."
        docHref="https://docs.getcara.ai"
      />
      <div className="flex flex-wrap items-start gap-4">
        <div className="min-w-0 flex-1">
          <SearchFilterInput
            filterDefinitions={filterDefinitions}
            onFiltersChange={setActiveFilters}
            initialFilters={initialFilters}
            placeholder="Filter by title, creator, or search..."
          />
        </div>
        <PermissionButton
          permissions={CREATE_EXCEL_TEMPLATE}
          whenDenied="hide"
          color="sky"
          type="button"
          className="min-h-12"
          onClick={() => setUploadDialogOpen(true)}
        >
          Upload Excel template
        </PermissionButton>
      </div>
      <React.Suspense fallback={<LibraryListLoading title="Loading Excel templates..." />}>
        <Await resolve={loader?.data}>
          {(
            data:
              | { excelSpreadsheetTemplates?: ExcelSpreadsheetTemplateFieldsFragment[] }
              | undefined
          ) => {
            const visible = filterStyleTemplates(
              data?.excelSpreadsheetTemplates ?? [],
              activeFilters,
              viewer
            );
            return (
              <div className="relative">
                {canUpdate && (
                  <FloatingSelectionBar
                    count={selected.length}
                    onClear={clear}
                    onVisibility={() => visibility.openDialog(selected)}
                    onArchive={archive.openDialog}
                    isArchiving={archive.isArchiving}
                    isUpdatingVisibility={visibility.isUpdating}
                    visibilityEnabled={visibilityEntitled}
                  />
                )}
                {visible.length === 0 ? (
                  <LibraryListEmptyState
                    title="No Excel templates found"
                    description={
                      activeFilters.length > 0
                        ? 'Try adjusting your search or filters.'
                        : 'No Excel templates available. Upload a .xlsx file to get started.'
                    }
                  />
                ) : (
                  <div className="px-4 sm:px-6 rounded-lg bg-white shadow dark:bg-neutral-800">
                    <DataTable
                      data={visible}
                      columns={getExcelTemplateColumns({
                        selectedSet,
                        setSelectedExcelTemplates: setSelected,
                        filtered: visible,
                        canEdit: canUpdate
                      })}
                      keyExtractor={(template) => template.id}
                      rowHref={excelTemplateRowHref}
                      tableClassName="[--gutter:theme(spacing.4)] sm:[--gutter:theme(spacing.6)]"
                      sortKey={sortBy}
                      sortDirection={sortDirection}
                      onSort={setSort}
                      sortFn={excelTemplateSortFn}
                    />
                  </div>
                )}
              </div>
            );
          }}
        </Await>
      </React.Suspense>

      <ArchiveConfirmDialog
        open={archive.dialogOpen}
        count={selected.length}
        itemNoun="Excel template"
        itemPlural="Excel templates"
        loading={archive.isArchiving}
        onCancel={archive.closeDialog}
        onConfirm={() => archive.archive(selected)}
      />

      <BulkVisibilityDialog
        open={visibility.open}
        count={visibility.ids.length}
        itemLabel="Excel template"
        notice="This changes only the selected Excel templates. Existing spreadsheets keep their current visibility."
        resultMessage={visibility.resultMessage}
        loading={visibility.isUpdating}
        onClose={visibility.closeDialog}
        onSave={(v: VisibilityConfigInput) => visibility.save(v)}
      />

      <ExcelTemplateCreateDialog
        open={uploadDialogOpen}
        onClose={() => setUploadDialogOpen(false)}
        action="/app/library/excel-templates/new"
      />

      <LibraryListOutlet />
    </div>
  );
};

/** Edit slideout for a single Excel template; body is a workbook preview. */
export const ExcelTemplateEditPage = () => {
  const loader = useLoaderData() as
    | {
        data: Promise<{ excelSpreadsheetTemplate?: ExcelSpreadsheetTemplateFieldsFragment | null }>;
      }
    | undefined;
  const submit = useSubmit();
  const validationErrors = useActionValidationErrors<UpdateExcelSpreadsheetTemplateMutation>();

  // The editor builds the entire `multipart/form-data` payload (title,
  // description, fill guidance, visibility, optional replacement workbook, and
  // the tabs JSON); we just hand it off to the router action.
  const submitForm = React.useCallback(
    (formData: FormData) => {
      submit(formData, { method: 'PUT', encType: 'multipart/form-data' });
    },
    [submit]
  );

  return (
    <React.Suspense fallback={<LibraryEditOverlayLoader />}>
      <Await resolve={loader?.data}>
        {(
          data:
            | { excelSpreadsheetTemplate?: ExcelSpreadsheetTemplateFieldsFragment | null }
            | undefined
        ) => {
          if (!data?.excelSpreadsheetTemplate) return null;
          return (
            <ExcelTemplateEditor
              excelTemplate={data.excelSpreadsheetTemplate}
              validationErrors={validationErrors}
              listUrl="/app/library/excel-templates"
              onSubmit={submitForm}
            />
          );
        }}
      </Await>
    </React.Suspense>
  );
};
